<template>
	<div class="adv-image-element" v-on="$listeners">
		<canvas v-if="useCanvas" ref="canvas" v-bind="$attrs"></canvas>
		<img v-else :src="src" v-bind="$attrs"/>
	</div>
</template>

<script>
// Canvas-based animated image player for GIF and WebP using the WebCodecs ImageDecoder API (when available).
// Falls back to <img> if ImageDecoder is not supported or decoding fails.
import {AnimatedImageCache} from "@/components/graphics/AnimatedImageCache";
import DecoderAdapters from "@/components/graphics/DecoderAdapters";

export default {
	name: "AdvImageElement",
	emits: ['ready', 'ended'],
	props: {
		src: {
			type: [String, Array],
			default: null
		},
		autoplay: {
			type: Boolean,
			default: false
		},
		loop: {
			type: Boolean,
			default: false
		},
		loopCount: {
			type: Number,
			default: 0 // 0 means infinite when loop is true (same semantics as VideoElement)
		},
		speed: {
			type: Number,
			default: 1 // playback speed multiplier
		},
		delay: {
			type: Number,
			default: 0 // seconds
		},
		// When true, allows attempting to decode cross-origin images via fetch + WebCodecs (requires server CORS headers)
		// Default false to avoid CORS errors and fall back to <img> for cross-origin sources.
		decodeCrossOrigin: {
			type: Boolean,
			default: true
		},
		isVisible: {
			type:Boolean,
			default: false
		},
		pauseWhenHidden: {
			type:Boolean,
			default: true
		}
	},
	data() {
		return {
			useCanvas: false,
			playing: false,
			decoder: null,
			frames: [], // { bitmap, durationMs }
			totalDurationMs: 0,
			currentTimeMs: 0,
			currentFrameIndex: 0,
			loops: 0,
			rafId: null,
			lastTickTime: null,
			cacheRelease: null,
			loopTimeoutId: null,
			imageBitmapOptions: {imageOrientation: "from-image", premultiplyAlpha: "none", colorSpaceConversion: "default"}
		};
	},
	watch: {
		src: {
			immediate: true,
			async handler(newVal) {
				await this.resetAndLoad(newVal);
			}
		},
		speed() {
			// take effect on next tick naturally
		},
		isVisible(val) {
			if(val && this.canAutoStart) {
				if(this.currentFrameIndex > 0 && this.currentFrameIndex < (this.frames.length - 1)) {
					this.play();
				} else {
					this.start();
				}
			} else {
				this.pause();
			}
		}
	},
	created() {
		// bind tick to preserve instance context in RAF callbacks across environments
		this.tick = this.tick.bind(this);
	},
	mounted() {
		// no-op; loading is done by src watcher
	},
	beforeUnmount() {
		this.cleanup();
	},
	computed:{
		canAutoStart() {
			return this.pauseWhenHidden? (this.autoplay && this.isVisible) : this.autoplay;
		}
	},
	methods: {
		async resetAndLoad(url) {
			this.cleanup();
			if (!url) {
				this.useCanvas = false;
				return;
			}


			const supportsImageDecoder = DecoderAdapters.CanDecodeURL(url);
			if (!supportsImageDecoder) {
				this.useCanvas = false;
				return; // fallback to <img>
			}

			// Try to load and decode frames via shared cache
			try {
				console.log("AdvImageElement: attempting cache acquire for", url);
				const acquired = await AnimatedImageCache.acquire(url, {
					decodeCrossOrigin: this.decodeCrossOrigin,
					imageBitmapOptions: this.imageBitmapOptions
				});

				console.log("AdvImageElement: cache acquired for", url, 'frames:', acquired.frames.length, 'ms:', acquired.totalDurationMs, '');

				// Store release to clean up when component goes away
				this.cacheRelease = acquired.release;

				// Use cached frames
				this.frames = acquired.frames;
				this.totalDurationMs = acquired.totalDurationMs;

				// Prepare canvas
				this.useCanvas = true;
				this.$nextTick(() => {
					const canvas = this.$refs.canvas;
					if (!canvas) return;
					const ctx = canvas.getContext("2d");
					const width = acquired.width || (this.frames[0]?.bitmap?.width || 0);
					const height = acquired.height || (this.frames[0]?.bitmap?.height || 0);
					// If width/height attributes are not provided, size canvas to image
					if (width && !canvas.getAttribute("width")) canvas.width = width;
					if (height && !canvas.getAttribute("height")) canvas.height = height;
					// Draw first frame
					if (this.frames[0]) {
						ctx.clearRect(0, 0, canvas.width, canvas.height);
						ctx.drawImage(this.frames[0].bitmap, 0, 0, canvas.width, canvas.height);
					}
					this.currentFrameIndex = 0;
					this.currentTimeMs = 0;
					this.loops = 0;
					this.$emit('ready');
					if (this.canAutoStart) {
						this.start();
					}
				});
			} catch (e) {
				console.warn("AdvImageElement: failed to decode animated image; falling back to <img>",url, e);
				// Ensure local state falls back
				this.frames = [];
				this.totalDurationMs = 0;
				// If cache acquisition failed we don't have a decoder/bitmaps to close here
				// Fallback to <img>
				this.useCanvas = false;
			}
		},
		tick(now) {
			if(this.pauseWhenHidden && !this.isVisible){
				this.pause();
				return;
			}
			if (!this.playing) return;
			if (this.lastTickTime == null) this.lastTickTime = now;
			const elapsed = (now - this.lastTickTime) * (this.speed || 1);
			this.lastTickTime = now;

			this.currentTimeMs += elapsed;

			// Handle loop wrap
			while (this.currentTimeMs >= this.totalDurationMs && this.totalDurationMs > 0) {
				// We're about to wrap. If looping with a delay, freeze on last frame for the delay, then restart at first frame.
				if (this.loop) {
					if (this.loopCount > 0) {
						// Finite loop count
						if (this.loops < this.loopCount - 1) {
							// More loops remaining
							this.loops++;
							if (this.delay > 0) {
								// Show last frame during the delay
								this.currentTimeMs = this.totalDurationMs;
								this.currentFrameIndex = this.frames.length - 1;
								this.drawCurrentFrame();
								this.pause();
								if (this.loopTimeoutId) {
									clearTimeout(this.loopTimeoutId);
									this.loopTimeoutId = null;
								}
								this.loopTimeoutId = setTimeout(() => {
									this.loopTimeoutId = null;
									this.seek(0);
									this.play();
								}, this.delay * 1000);
								return;
							} else {
								// Immediate wrap without delay
								this.currentTimeMs -= this.totalDurationMs;
							}
						} else {
							// reached final loop
							this.stopAtEnd();
							return;
						}
					} else {
						// Infinite looping
						if (this.delay > 0) {
							// Show last frame during the delay
							this.currentTimeMs = this.totalDurationMs;
							this.currentFrameIndex = this.frames.length - 1;
							this.drawCurrentFrame();
							this.pause();
							if (this.loopTimeoutId) {
								clearTimeout(this.loopTimeoutId);
								this.loopTimeoutId = null;
							}
							this.loopTimeoutId = setTimeout(() => {
								this.loopTimeoutId = null;
								this.seek(0);
								this.play();
							}, this.delay * 1000);
							return;
						} else {
							// Immediate wrap without delay
							this.currentTimeMs -= this.totalDurationMs;
						}
					}
				} else {
					// Not looping: stop on last frame
					this.stopAtEnd();
					return;
				}
			}

			// Find frame by accumulated durations
			let acc = 0;
			let idx = 0;
			for (let i = 0; i < this.frames.length; i++) {
				acc += this.frames[i].durationMs;
				if (this.currentTimeMs < acc) {
					idx = i;
					break;
				}
			}

			if (idx !== this.currentFrameIndex) {
				this.currentFrameIndex = idx;
				this.drawCurrentFrame();
			}

			this.rafId = requestAnimationFrame(this.tick);
		},
		drawCurrentFrame() {
			const canvas = this.$refs.canvas;
			if (!canvas) return;
			const ctx = canvas.getContext("2d");
			const frame = this.frames[this.currentFrameIndex];
			if (!frame) return;
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(frame.bitmap, 0, 0, canvas.width, canvas.height);
		},
		stopAtEnd() {
			this.pause();
			this.currentTimeMs = this.totalDurationMs;
			this.currentFrameIndex = this.frames.length - 1;
			this.drawCurrentFrame();
			this.$emit('ended');
		},
		play() {
			if (!this.useCanvas || this.playing || this.frames.length === 0) return;
			this.playing = true;
			this.lastTickTime = null;
			this.rafId = requestAnimationFrame(this.tick);
		},
		pause() {
			this.playing = false;
			if (this.rafId) {
				cancelAnimationFrame(this.rafId);
				this.rafId = null;
			}
			if (this.loopTimeoutId) {
				clearTimeout(this.loopTimeoutId);
				this.loopTimeoutId = null;
			}
		},
		seek(timeSeconds) {
			if (!this.useCanvas || this.frames.length === 0) return;
			const tMs = Math.max(0, Math.min(timeSeconds * 1000, this.totalDurationMs));
			this.currentTimeMs = tMs;
			// set frame immediately
			let acc = 0;
			let idx = 0;
			for (let i = 0; i < this.frames.length; i++) {
				acc += this.frames[i].durationMs;
				if (this.currentTimeMs < acc) {
					idx = i;
					break;
				}
			}
			this.currentFrameIndex = idx;
			this.drawCurrentFrame();
		},
		getDuration() {
			return this.totalDurationMs / 1000;
		},
		restLoops() { // keep same typo as VideoElement for API parity
			this.loops = 0;
		},
		start() {
			if (this.delay > 0) {
				setTimeout(() => {
					//this.seek(from);
					this.play()
				}, this.delay * 1000);
			} else {
				//this.seek(from);
				this.play();
			}
		},
		onError(e) {
			// placeholder to mirror VideoElement API
			// errors will typically be captured during decode
			console.warn("AdvImageElement error", e);
		},
		cleanup() {
			this.pause();
			if (this.loopTimeoutId) {
				clearTimeout(this.loopTimeoutId);
				this.loopTimeoutId = null;
			}
			// Release shared cached frames if acquired
			if (typeof this.cacheRelease === 'function') {
				try {
					this.cacheRelease();
				} catch (_e) { /* noop */
				}
			}
			this.cacheRelease = null;
			this.decoder = null; // no longer used with cache, kept for API symmetry
			this.frames = [];
			this.totalDurationMs = 0;
			this.currentTimeMs = 0;
			this.currentFrameIndex = 0;
			this.loops = 0;
			this.useCanvas = false; // will be re-enabled if new load succeeds
			this.lastTickTime = null;
		}
	}
};
</script>

<style lang="scss" scoped>
.adv-image-element {
  display: inline-block;
}

canvas, img {
  display: block;
  width: 100%;
  height: 100%;
  /*  object-fit: contain; */
}
</style>
